'use client';

import { useState, useRef, useEffect } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Search, Loader2 } from 'lucide-react';
import type { CompanySearchResult } from '@/lib/fmp/company-search';
import { POPULAR_SYMBOLS } from '@/lib/fmp/company-search';

interface CompanySearchProps {
  variant?: 'header' | 'inline' | 'empty-state';
  placeholder?: string;
  onSelectCompany(symbol: string): void;
  className?: string;
  showPopularSymbols?: boolean;
  disabled?: boolean;
}

export function CompanySearch({
  variant = 'header',
  placeholder = 'Search by ticker symbol...',
  onSelectCompany,
  className = '',
  showPopularSymbols = variant === 'empty-state',
  disabled = false,
}: CompanySearchProps) {
  // Search States
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<CompanySearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Refs for dropdown
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search companies
  useEffect(() => {
    const searchCompanies = async (term: string) => {
      if (!term || term.length < 2) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      setIsSearching(true);
      setSearchError(null);
      setShowDropdown(true);

      try {
        const response = await fetch(
          `/api/fmp/company-search?query=${encodeURIComponent(term)}`,
        );

        if (!response.ok) {
          throw new Error('Failed to fetch companies');
        }

        const data = await response.json();
        setSearchResults(data);
      } catch (err) {
        console.error('Error searching companies:', err);
        setSearchError('Failed to search companies. Please try again.');
      } finally {
        setIsSearching(false);
      }
    };

    if (debouncedSearchTerm) {
      searchCompanies(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm]);

  // Form Handlers
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      const newSymbol = searchTerm.toUpperCase();
      onSelectCompany(newSymbol);
      setSearchTerm('');
      setShowDropdown(false);
    }
  };

  const handleSelectResult = (company: CompanySearchResult) => {
    onSelectCompany(company.symbol);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const handleSelectPopular = (symbol: string) => {
    onSelectCompany(symbol);
  };

  // Determine styling based on variant
  const getContainerClasses = () => {
    switch (variant) {
      case 'header':
        return 'flex gap-2';
      case 'inline':
        return 'w-full';
      case 'empty-state':
        return 'w-full max-w-sm';
      default:
        return '';
    }
  };

  const getInputClasses = () => {
    switch (variant) {
      case 'header':
        return 'pl-8 min-w-[300px]';
      case 'inline':
        return 'pl-8 w-full';
      case 'empty-state':
        return 'pl-8';
      default:
        return 'pl-8';
    }
  };

  return (
    <div className={`${className}`}>
      <form onSubmit={handleSearch} className={getContainerClasses()}>
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 size-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder={placeholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => {
              if (searchTerm.length >= 2 && searchResults.length > 0) {
                setShowDropdown(true);
              }
            }}
            className={getInputClasses()}
            disabled={disabled}
          />

          {showDropdown && (
            <div
              ref={dropdownRef}
              className="absolute z-50 top-full inset-x-0 mt-1 bg-popover rounded-md border shadow-md max-h-[300px] overflow-y-auto"
            >
              {isSearching ? (
                <div className="flex items-center justify-center p-4 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin mr-2" />
                  Searching...
                </div>
              ) : searchError ? (
                <div className="p-4 text-sm text-red-500">{searchError}</div>
              ) : searchResults.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">
                  No results found
                </div>
              ) : (
                <ScrollArea className="h-72">
                  <div className="p-2">
                    {searchResults.map((company) => (
                      <Button
                        key={company.symbol}
                        variant="ghost"
                        className="w-full justify-start text-left hover:bg-accent border-b last:border-b-0"
                        onClick={() => handleSelectResult(company)}
                      >
                        <div className="flex flex-col">
                          <div className="flex items-center">
                            <span className="font-medium mr-2">
                              {company.symbol}
                            </span>
                            <span className="text-muted-foreground text-sm">
                              {company.name}
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            <span className="font-bold">
                              {company.stockExchange}
                            </span>{' '}
                            • {company.currency}
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          )}
        </div>

        {variant === 'header' && (
          <Button type="submit" disabled={disabled || !searchTerm.trim()}>
            {isSearching ? (
              <Loader2 className="size-4 animate-spin mr-2" />
            ) : null}
            Search
          </Button>
        )}
      </form>

      {showPopularSymbols && (
        <div className="space-y-4 mt-6">
          <p className="text-muted-foreground text-center">
            Or try one of these popular symbols:
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {POPULAR_SYMBOLS.map((symbol) => (
              <Button
                key={symbol}
                variant="outline"
                size="sm"
                onClick={() => handleSelectPopular(symbol)}
                disabled={disabled}
              >
                {symbol}
              </Button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
